import React, { useState } from 'react';

/**
 * A cloud route-ok közös felület-darabjai (Routes, Route library, Library review).
 * A backend párja: backend/src/utils/routeText.js — ott dől el, mi számít
 * tárgy-használatnak (teleport-tárgy, világtérkép, belépő tárgy és kulcs).
 *
 * ANGOL NEVEK ÉS KÉPEK: a backend az összegzés mellé teszi (`summary.names`:
 * { maps, kills, items, entry }, lásd backend/src/utils/gameNames.js). A név az
 * elsődleges, alatta kicsiben az azonosító — a bot szerkesztője azonosítókat mutat,
 * így a kettő összeköthető. Ha nincs név, a nyers azonosító marad.
 */

/** Egy játékbeli kép; ha nem töltődik be, eltűnik (a helye a hívóé). */
function GameImg({ src, className = '' }) {
  const [ok, setOk] = useState(true);
  if (!src || !ok) return null;
  return <img src={src} alt="" loading="lazy" draggable="false" className={className} onError={() => setOk(false)} />;
}

// A route a tárgyat ELHASZNÁLHATJA — ezért a felület mindenhol kiírja, és idegen
// route-nál hozzáadás előtt megerősítést kér (terv 4.10).
export const ITEM_KIND_LABEL = {
  teleport: 'Boss teleport item',
  world_map: 'World map',
  entry_item: 'Entry item',
  entry_need: 'Needed to enter',
};

/** Egy sor a tárgy-listában: „25 × pw_boss_khalys_travelitem”. */
export function itemLine(it) {
  const label = ITEM_KIND_LABEL[it.kind] || it.kind;
  if (!it.tpl) return label;
  return `${it.count > 1 ? `${it.count} × ` : ''}${it.tpl}`;
}

/** Rövid felirat egy jelvénybe: „25 × item” / „World map”. */
export function itemShort(items) {
  if (!items || items.length === 0) return null;
  // A hívók egy része "uses " elé teszi ("uses 2 items"), más része jelvénybe —
  // mindkettőben olvasható főnévi alak kell.
  if (items.length > 1) return `${items.length} items`;
  const it = items[0];
  if (it.kind === 'world_map') return 'World map';
  return it.count > 1 ? `${it.count} × item` : (ITEM_KIND_LABEL[it.kind] || 'Item');
}

/** Egy tárgy ikonja egy kis kockában; ha nincs ikon, a csomag-jel. */
function ItemTile({ icon }) {
  const [ok, setOk] = useState(true);
  return (
    <span className="w-9 h-9 flex-shrink-0 rounded-lg border border-warn/25 bg-bar flex items-center justify-center overflow-hidden">
      {icon && ok
        ? <img src={icon} alt="" loading="lazy" draggable="false" className="w-8 h-8" onError={() => setOk(false)} />
        : <IconPackage className="w-4 h-4 text-warn" />}
    </span>
  );
}

export function ItemsBox({ items, title = 'Uses items', names }) {
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-gray-400">
        <IconCheck className="w-4 h-4 text-ok" /> No items. It runs from the map you stand on.
      </div>
    );
  }
  return (
    <div className="rounded-panel border border-warn/25 bg-warn/[0.07] px-3 py-2.5 flex flex-col gap-2">
      {title && (
        <span className="flex items-center gap-2 text-[13px] text-gray-100">
          <IconPackage className="w-4 h-4 text-warn flex-shrink-0" /> {title}
        </span>
      )}
      {items.map((it, i) => {
        const n = it.tpl ? names?.items?.[it.tpl] : null;
        const kind = ITEM_KIND_LABEL[it.kind] || it.kind;
        return (
          <div key={i} className="flex items-center gap-2.5 min-w-0">
            <ItemTile icon={n?.icon} />
            <div className="min-w-0 flex flex-col">
              <span className="text-[13px] text-[#f0cf8a] break-words">
                {it.count > 1 && <span className="text-warn">{it.count} × </span>}
                {n?.name || kind}
              </span>
              <span className="text-[11.5px] text-gray-500 break-all">
                {n?.name && <>{kind}{it.tpl ? ' · ' : ''}</>}
                {it.tpl && <span className="font-mono">{it.tpl}</span>}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A pálya neve egy sorban, alatta kicsiben az azonosító — vagy csak az azonosító. */
function MapLabel({ id, info, extra }) {
  if (!info) return <span className="font-mono text-[12.5px] text-gray-50 break-all">{id}</span>;
  return (
    <span className="min-w-0 flex flex-col">
      <span className="text-[13px] font-medium text-gray-50 break-words">
        {info.name}
        {(extra || info.difficulty) && <span className="font-normal text-gray-400"> · {extra || info.difficulty}</span>}
      </span>
      <span className="font-mono text-[11px] text-gray-600 break-all">{id}</span>
    </span>
  );
}

export function StepsList({ maps, entry, names }) {
  if (!maps || maps.length === 0) return <span className="text-[13px] text-gray-500">No steps yet.</span>;
  return (
    <div className="flex flex-col gap-2">
      <ol className="flex flex-col gap-1.5">
        {maps.map((m, i) => {
          const info = names?.maps?.[m];
          return (
            <li key={i} className="flex gap-2 items-start">
              <span className="w-[22px] h-[22px] flex-shrink-0 grid place-items-center rounded-lg bg-bar border border-line
                               font-mono text-[11px] font-medium text-gray-50">{i + 1}</span>
              {info?.img && (
                <GameImg src={info.img} className="w-16 h-9 object-cover rounded-lg border border-line flex-shrink-0" />
              )}
              <MapLabel id={m} info={info} />
            </li>
          );
        })}
      </ol>
      {entry && (
        <div className="flex gap-1.5 items-start text-[12.5px] text-gray-500">
          <span className="flex-shrink-0 pt-px">Starts with a jump to</span>
          <MapLabel id={entry} info={names?.entry} />
        </div>
      )}
    </div>
  );
}

/** A kill targetek: kép (ha van — főleg bossok), angol név, alatta az azonosító. */
export function KillTargets({ kills, names }) {
  if (!kills || kills.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 mt-1">
      <span className="text-[12.5px] text-gray-500">Kill targets:</span>
      <div className="flex flex-wrap gap-1.5">
        {kills.map(k => {
          const n = names?.kills?.[k];
          return (
            <span key={k} title={k}
                  className="inline-flex items-center gap-2 max-w-full rounded-lg border border-line bg-bar py-1 pl-1 pr-2.5">
              <span className="w-7 h-7 flex-shrink-0 rounded-md bg-white/[0.04] flex items-center justify-center overflow-hidden">
                {n?.img
                  ? <GameImg src={n.img} className="w-7 h-7" />
                  : <IconTarget className="w-3.5 h-3.5 text-gray-500" />}
              </span>
              <span className="min-w-0 flex flex-col">
                <span className={n ? 'text-[12.5px] text-gray-50 truncate' : 'font-mono text-[12px] text-gray-50 truncate'}>
                  {n?.name || k}
                </span>
                {n && <span className="font-mono text-[10.5px] text-gray-600 truncate">{k}</span>}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── IDŐ ─────────────────────────────────────────────────────────────────────
// A dashboard UTC-ben mutat ("Times in UTC" a fejlécben) — itt is.
const pad = (n) => String(n).padStart(2, '0');

export function utcDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function utcStamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${utcDate(iso)}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** „3 min ago”, „5 h ago”, „Yesterday”, egyébként a dátum. */
export function ago(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 2 * 86400) return 'Yesterday';
  return utcDate(iso);
}

// ── IKONOK (vonalas, a többi oldal mintájára) ───────────────────────────────
function Svg({ className = 'w-4 h-4', children, ...rest }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" className={className} aria-hidden="true" {...rest}>
      {children}
    </svg>
  );
}
export const IconCheck = (p) => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>;
export const IconPackage = (p) => (
  <Svg {...p}>
    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
    <path d="M12 22V12" /><path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7" />
  </Svg>
);
export const IconCloudOk = (p) => (
  <Svg {...p}><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" /><path d="m9.5 14 2 2 3.5-3.5" /></Svg>
);
export const IconAlert = (p) => (
  <Svg {...p}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></Svg>
);
export const IconFolder = (p) => (
  <Svg {...p}><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" /></Svg>
);
export const IconChevron = ({ open, ...p }) => (
  <Svg {...p}>{open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}</Svg>
);
export const IconTicket = (p) => (
  <Svg {...p}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" /><path d="M13 17v2" /><path d="M13 11v2" />
  </Svg>
);
export const IconBook = (p) => (
  <Svg {...p}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></Svg>
);
export const IconRestore = (p) => (
  <Svg {...p}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></Svg>
);
export const IconDownload = (p) => (
  <Svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /><path d="M12 15V3" /></Svg>
);
export const IconTrash = (p) => (
  <Svg {...p}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></Svg>
);
export const IconPencil = (p) => (
  <Svg {...p}><path d="M21.17 6.81a1 1 0 0 0-3.99-3.99L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" /></Svg>
);
export const IconSearch = (p) => (
  <Svg {...p}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></Svg>
);
export const IconFlag = (p) => (
  <Svg {...p}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></Svg>
);
export const IconPlus = (p) => <Svg {...p}><path d="M5 12h14" /><path d="M12 5v14" /></Svg>;
export const IconEye = (p) => (
  <Svg {...p}><path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" /><circle cx="12" cy="12" r="3" /></Svg>
);
export const IconPlay = (p) => <Svg {...p}><path d="M6 3 20 12 6 21 6 3z" /></Svg>;
export const IconTarget = (p) => (
  <Svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></Svg>
);
export const IconShield = (p) => (
  <Svg {...p}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /></Svg>
);

/**
 * Szegmentált szűrő (a sablon `.segf`-je): felirat + darabszám; a `hot` darabszám
 * borostyán (ütközés, várakozó beküldés).
 * items: [{ id, label, count?, hot? }]
 */
export function SegFilter({ items, value, onChange, label }) {
  return (
    <div className="segf" role="group" aria-label={label}>
      {items.map(f => (
        <button key={f.id} type="button" aria-pressed={value === f.id} onClick={() => onChange(f.id)}>
          {f.label}
          {f.count != null && <span className={f.hot ? 'text-warn' : 'text-gray-600'}>{f.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Fül-címsor (a sablon `.tab-head`-je): cím, alcím, jobbra opcionális gomb vagy címke. */
export function TabHead({ title, children, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-gray-50">{title}</h2>
        {children && <p className="mt-1 max-w-[70ch] text-sm text-gray-400">{children}</p>}
      </div>
      {right}
    </div>
  );
}

/** Kis keresőmező a listák fölé. */
export function SearchInput({ value, onChange, placeholder = 'Search', className = 'w-64' }) {
  return (
    <label className={`relative block ${className}`}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"><IconSearch /></span>
      <input
        type="search"
        className="input pl-9"
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * Kérdez-e a BOT egy idegen (kódból / könyvtárból jött) route első futása előtt.
 * A terv szerint (Q5, 4.6 f) ez egy későbbi kör: amíg a bot nem tudja, a web ne
 * ígérje. A bot-oldali kapu élesítésekor kell átállítani.
 */
export const BOT_ASKS_BEFORE_FIRST_RUN = false;

/**
 * Lista + részlet-panel elrendezésnél keskeny képernyőn (a Tailwind `lg` alatt)
 * a panel a lista ALATT áll: kattintás után odagörgetünk, különben úgy tűnik,
 * a kattintás nem csinált semmit.
 */
export function revealBelow(ref) {
  if (!window.matchMedia('(max-width: 1023px)').matches) return;
  requestAnimationFrame(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
}

/** A tétel-kulcs ellenőrzése a felületen is (a backend a végső döntés): routeText.normalizeKey. */
const LEAF_RE = /^[A-Za-z0-9 _.-]+$/;
export function validLeaf(s) {
  return !!s && LEAF_RE.test(s) && !s.includes('..') && !/^[.\s]|[.\s]$/.test(s);
}
